import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { MonitorX, AlertTriangle, MapPin } from "lucide-react";
import { RegisterDeviceForm } from "./RegisterDeviceForm";
import { askLabel } from "@/lib/helper";

// Small contextual help row: opens the tutorial or asks the help assistant the exact question in place.
function HelpRow({ slug, q, testid }) {
  const nav = useNavigate();
  const { business } = useAuth();
  const ask = () => window.dispatchEvent(new CustomEvent("neec-open-ask-eli", { detail: { question: q } }));
  return (
    <p className="text-xs text-muted-foreground mt-3 flex flex-wrap gap-3" data-testid={testid}>
      <button type="button" className="text-emerald-600 hover:underline" onClick={() => nav(`/help?topic=${slug}`)}>Read the help article</button>
      <button type="button" className="text-emerald-600 hover:underline" onClick={ask}>{askLabel(business)}</button>
    </p>
  );
}

export function usePosRegister() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const load = useCallback(() => { if (user) api.get("/pos/register/me").then((r) => setState(r.data)).catch(() => setState(null)); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); window.addEventListener("neec-register-changed", load); return () => window.removeEventListener("neec-register-changed", load); }, [load]);
  return { state, reload: load };
}

// Blocks the POS surface (not the app) when enforcement is ON and this browser has no server-recognised register.
// Legacy (grandfathered) registers show a non-blocking banner asking the Owner to assign a location.
export function PosRegisterGate({ state, reload }) {
  const { user } = useAuth();
  const nav = useNavigate();
  if (!state || state.status === "not_applicable") return null;
  if (state.status === "legacy") {
    return (
      <div className="rounded-xl border border-amber-500/50 bg-amber-500/10 p-3 text-sm flex items-start gap-2 mb-3" data-testid="pos-legacy-register-banner">
        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <div><p className="font-bold">Register setup update required</p>
          <p className="text-muted-foreground">Assign this register ("{state.register?.display_name}") to a location to complete the new register setup. It keeps working until then.</p>
          {user?.role === "owner" && <Button size="sm" variant="outline" className="mt-2" onClick={() => nav("/settings?tab=registers")} data-testid="pos-legacy-assign-btn"><MapPin className="w-3.5 h-3.5 mr-1" /> Assign location</Button>}
        </div>
      </div>
    );
  }
  if (state.status === "not_assigned") {
    return (
      <div className="fixed inset-0 z-40 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pos-not-assigned">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
          <p className="font-head font-bold text-lg" data-testid="pos-not-assigned-title">You are not assigned to this location.</p>
          <p className="text-sm text-muted-foreground mt-1">This terminal belongs to {state.register?.location_name || "another location"}. Contact your Owner or Manager to be added to this location.</p>
          <HelpRow slug="not-assigned-location" q="Why does it say I am not assigned to this location?" testid="help-link-not-assigned" />
          <div className="mt-4 flex justify-end"><Button variant="ghost" onClick={() => nav("/")} data-testid="pos-not-assigned-back-btn">Back to Dashboard</Button></div>
        </div>
      </div>
    );
  }
  if (state.status === "not_authorized") {
    return (
      <div className="fixed inset-0 z-40 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pos-not-authorized">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
          <p className="font-head font-bold text-lg" data-testid="pos-not-authorized-title">You are not authorized to use this register.</p>
          <p className="text-sm text-muted-foreground mt-1">This terminal is "{state.register?.display_name}"{state.register?.location_name ? ` at ${state.register.location_name}` : ""}. Ask your Owner to add this register to your authorized registers under Staff.</p>
          <div className="mt-4 flex justify-end"><Button variant="ghost" onClick={() => nav("/")} data-testid="pos-not-authorized-back-btn">Back to Dashboard</Button></div>
        </div>
      </div>
    );
  }
  if (state.status !== "required" || !state.enforcement) return null;
  return (
    <div className="fixed inset-0 z-40 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pos-register-required">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-500/15 text-red-500 flex items-center justify-center shrink-0"><MonitorX className="w-5 h-5" /></div>
          <div><p className="font-head font-bold text-lg" data-testid="pos-register-required-title">This device is not registered as a POS terminal.</p>
            <p className="text-sm text-muted-foreground mt-1">You can still use Dashboard, Reports, Inventory and other management pages. Sales can only be processed from a registered terminal assigned to a location.</p></div>
        </div>
        <div className="mt-5">
          {state.can_register
            ? <RegisterDeviceForm locations={state.locations} compact onDone={reload} />
            : <p className="text-sm rounded-lg bg-secondary/60 p-3" data-testid="pos-register-ask-owner">Ask the Owner{user?.role === "cashier" ? " or Manager" : ""} to register this device as a POS terminal before taking sales.</p>}
          <HelpRow slug="device-not-registered" q="What does this device is not registered mean?" testid="help-link-not-registered" />
        </div>
        <div className="mt-4 flex justify-end"><Button variant="ghost" onClick={() => nav("/")} data-testid="pos-register-back-btn">Back to Dashboard</Button></div>
      </div>
    </div>
  );
}
